import React, { useEffect, useMemo, useRef, useState } from "react";
import Fuse from "fuse.js";
import { Box, Typography, styled } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useGetItemCardsQuery } from "../../services/items/items";
import { PublicList, DEFAULT_LIST_NAME } from "../PublicList/PublicList";
import ProductGridSkeleton from "../ProductGridSkeleton/ProductGridSkeleton";
import { ItemCardType } from "../../types/types";
import { gaEvent } from "../../analytics";
import ProductFilters from "../ProductFilters/ProductFilters";
import { FAVORITES_FILTER } from "../ProductFilters/ProductFilters";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../app/store";
import { setToken } from "../../services/auth/authSlice";
import { useGetFavoritesQuery } from "../../services/favorites/favorites";
import {
  SEARCH_QUERY_PARAM,
  SEARCH_RESULTS_ANCHOR_ID,
  SEARCH_RESULTS_TEXTS,
} from "../../pages/shared/constants";

const ProductListContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const ResultCount = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.pxToRem(13),
  paddingBottom: theme.spacing(1.5),
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(5, 2),
  textAlign: "center",
}));

const ProductSearch: React.FC = () => {
  // The term is owned by the URL and written by `HeaderSearch`, which lives in the header. This
  // component only reads it, so a link into `/?q=vidrio` renders filtered on first paint.
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get(SEARCH_QUERY_PARAM) ?? "";

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector((state: RootState) => state.auth.token);

  useEffect(() => {
    if (isAuthenticated && !token) {
      getAccessTokenSilently().then((t) => dispatch(setToken(t)));
    }
  }, [isAuthenticated, token, getAccessTokenSilently, dispatch]);

  const hasAuth = isAuthenticated && !!token;
  const { data: favorites } = useGetFavoritesQuery(undefined, { skip: !hasAuth });

  const favoriteIds = useMemo(() => {
    if (!favorites) return new Set<string>();
    return new Set(favorites.map((f) => f.item_id));
  }, [favorites]);

  // The slim `view=card` projection, not the full rows. Everything below still works over one
  // in-memory array of the whole catalog — that is what keeps this a projection swap rather than a
  // rewrite of search, facets and favourites (docs/catalog-modernization/README.md §6.1).
  const { data, isError, isFetching } = useGetItemCardsQuery();

  const fuse = useMemo(
    () =>
      new Fuse<ItemCardType>(data ?? [], {
        // `search_text` (name + marca + modelo + tipo_producto) stands in for `description`, which
        // the projection drops. Same typo tolerance; only deep description matches are lost.
        keys: ["name", "search_text"],
        threshold: 0.3,
        ignoreLocation: true,
      }),
    [data],
  );

  // A term and a category compete: searching "vidrio" while "Celulares" is selected usually
  // returns nothing, which reads as a broken search rather than an empty intersection. Guarded on
  // a non-empty term so arriving at a bare `/` cannot clear a category the shopper just picked.
  useEffect(() => {
    if (searchQuery.trim()) setSelectedCategory(null);
  }, [searchQuery]);

  const categories = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    data.forEach((item) => {
      if (item.tipo_producto) set.add(item.tipo_producto);
    });
    return Array.from(set).sort((a, b) => {
      if (a === "celular") return -1;
      if (b === "celular") return 1;
      if (a === "otros") return 1;
      if (b === "otros") return -1;
      return a.localeCompare(b);
    });
  }, [data]);

  const filteredProducts = useMemo(() => {
    let results: ItemCardType[] = searchQuery
      ? fuse.search(searchQuery).map((r) => r.item)
      : (data ?? []);
    if (selectedCategory === FAVORITES_FILTER) {
      results = results.filter((item) => favoriteIds.has(item.id));
    } else if (selectedCategory) {
      results = results.filter((item) => item.tipo_producto === selectedCategory);
    }
    return results;
  }, [fuse, data, searchQuery, selectedCategory, favoriteIds]);

  /**
   * One `search` event per term (EB-558).
   *
   * This effect used to fire on `filteredProducts.length`, which changes without the term changing:
   * when the catalog query resolves after first paint, when favourites load, and whenever a
   * category is picked while a term is active. The first of those was the damaging one — it sent a
   * `search` with `results_count: 0` and then a second with the real count, so a "searches with no
   * results" report was measuring the loading race rather than shopper intent.
   *
   * The count stays in the dependency list so the event carries a settled number, but the ref
   * guarantees it is reported once. Waiting on `isFetching` is what makes `results_count: 0` mean
   * "no matches" rather than "not loaded yet"; narrowing a search by category is a `filter_select`,
   * which `ProductFilters` already reports on its own.
   */
  const lastTrackedTermRef = useRef<string | null>(null);

  useEffect(() => {
    const term = searchQuery.trim();

    // Clearing the field re-arms the guard, so searching the same term again is a new search.
    if (!term) {
      lastTrackedTermRef.current = null;
      return;
    }
    if (isFetching || lastTrackedTermRef.current === term) return;

    lastTrackedTermRef.current = term;
    gaEvent("search", { search_term: term, results_count: filteredProducts.length });
  }, [searchQuery, isFetching, filteredProducts.length]);

  /**
   * What this grid is, for `view_item_list` / `select_item` (EB-558).
   *
   * A term wins over a category on purpose. The two can be active together (searching, then
   * narrowing), and reporting that as `category:celular` would file it with plain browsing — the
   * shopper typed something, which is the stronger intent and the one worth measuring click-through
   * against. The narrowing itself is already reported separately as `filter_select`.
   *
   * Kept to a small closed set, plus one value per category: GA4 groups reports by this string, so
   * anything term-derived would shatter the dimension into thousands of one-row lists.
   */
  const listName = useMemo(() => {
    if (searchQuery.trim()) return "search_results";
    if (selectedCategory === FAVORITES_FILTER) return "favorites";
    if (selectedCategory) return `category:${selectedCategory}`;
    return DEFAULT_LIST_NAME;
  }, [searchQuery, selectedCategory]);

  // A count of the entire catalog is noise; a count of what a filter returned is the answer to the
  // question the shopper just asked.
  const isFiltering = Boolean(searchQuery.trim()) || selectedCategory !== null;
  const showResultSummary = isFiltering && !isFetching && !isError;
  const hasResults = filteredProducts.length > 0;

  return (
    <Box>
      {/* Where pressing Enter in the header search scrolls to. */}
      <ProductListContainer id={SEARCH_RESULTS_ANCHOR_ID}>
        <ProductFilters
          categories={categories}
          selected={selectedCategory}
          onSelect={setSelectedCategory}
          showFavorites={favoriteIds.size > 0}
        />
        {isFetching && <ProductGridSkeleton />}
        {isError && <Typography color="error">Error loading items.</Typography>}

        {/* Only while a filter is narrowing things down. On a plain visit the count is the whole
            catalog, which tells the shopper nothing they asked for. Suppressed while fetching so a
            slow load never reads as "no encontramos nada". */}
        {showResultSummary && hasResults && (
          <ResultCount aria-live="polite">
            {SEARCH_RESULTS_TEXTS.count(filteredProducts.length)}
          </ResultCount>
        )}

        {showResultSummary && !hasResults ? (
          <EmptyState aria-live="polite">
            <Typography variant="h6" fontFamily="Inter, sans-serif" fontWeight={700}>
              {SEARCH_RESULTS_TEXTS.empty}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ opacity: 0.7 }}>
              {SEARCH_RESULTS_TEXTS.emptyHint}
            </Typography>
          </EmptyState>
        ) : (
          <PublicList
            products={filteredProducts}
            resetKey={`${searchQuery}|${selectedCategory ?? ""}`}
            listName={listName}
          />
        )}
      </ProductListContainer>
    </Box>
  );
};

export default ProductSearch;
